/**
 * Responsive Data Table Component
 * 
 * Provides a responsive table that automatically switches between table view
 * on desktop and card view on mobile devices.
 */

import React, { useState, useEffect } from 'react';
import { Table, Pagination, Empty, Spin, Card, Typography, Space, Divider, Button, Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { TableProps, ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useResponsive } from '@municipality/ui';

const { Text } = Typography;

/**
 * Props for ResponsiveDataTable component
 */
export interface ResponsiveDataTableProps<T = Record<string, unknown>> extends Omit<TableProps<T>, 'pagination' | 'onRow'> {
  dataSource: T[];
  columns: ColumnsType<T>;
  loading?: boolean;
  pagination?: TablePaginationConfig | false;
  rowKey?: keyof T | ((record: T) => string);
  size?: TableProps<T>['size'];
  bordered?: TableProps<T>['bordered'];
  maxMobileColumns?: number;
  showMobileActions?: boolean;
  mobileActionItems?: (record: T) => Array<{
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
  }>;
  customCardRenderer?: (record: T, index: number, columns: ColumnsType<T>, actionsColumn?: ColumnsType<T>[number], actionItems?: (record: T) => Array<{
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
  }>) => React.ReactNode;
}

/**
 * Mobile Card View Component
 */
const MobileCardView = <T = Record<string, unknown>>({
  dataSource,
  columns,
  rowKey = 'id' as keyof T,
  maxColumns = 3,
  showActions = false,
  actionItems,
  customCardRenderer,
}: {
  dataSource: T[];
  columns: ColumnsType<T>;
  rowKey?: keyof T | ((record: T) => string);
  maxColumns?: number;
  showActions?: boolean;
  actionItems?: (record: T) => Array<{
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
  }>;
  customCardRenderer?: (record: T, index: number, columns: ColumnsType<T>, actionsColumn?: ColumnsType<T>[number], actionItems?: (record: T) => Array<{
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
  }>) => React.ReactNode;
}) => {
  if (!dataSource || dataSource.length === 0) {
    return <div>No Data</div>;
  }

  const getRowKey = (record: T, index: number) => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return (record[rowKey as keyof T] || index).toString();
  };

  // Extract the actions column from the desktop table
  const actionsColumn = columns.find(col => 
    col.key === 'actions' || 
    col.title === 'Actions' || 
    (typeof col.title === 'string' && col.title.toLowerCase().includes('action')) ||
    (typeof col.title === 'string' && col.title.toLowerCase().includes('actions'))
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {dataSource.map((record, index) => {
        // Use custom renderer if provided
        if (customCardRenderer) {
          return (
            <React.Fragment key={getRowKey(record, index)}>
              {customCardRenderer(record, index, columns, actionsColumn, actionItems)}
            </React.Fragment>
          );
        }

        // Default card rendering
        return (
          <Card
            key={getRowKey(record, index)}
            size="small"
            style={{ width: '100%' }}
            bodyStyle={{ padding: 10 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {columns.slice(0, maxColumns).map((col, colIndex) => {
                // Skip column groups and columns without dataIndex/title
                if ('children' in col || (!col.title && !('dataIndex' in col ? col.dataIndex : null))) {
                  return null;
                }
                
                const dataIndex = ('dataIndex' in col ? col.dataIndex : null) as keyof T;
                const value = dataIndex ? record[dataIndex] : null;
                const render = col.render;

                // Handle render function result properly
                const renderResult = render ? render(value, record, index) : value;
                const displayValue = (renderResult || '-') as React.ReactNode;

                return (
                  <div key={colIndex}>
                    <Text strong style={{ fontSize: 14 }}>
                      {String(col.title)}:{' '}
                    </Text>
                    <div style={{ fontSize: 14 }}>
                      {displayValue}
                    </div>
                    {colIndex < Math.min(columns.length, maxColumns) - 1 && (
                      <Divider style={{ margin: '8px 0' }} />
                    )}
                  </div>
                );
              })}
              
              {/* Render actions using the desktop table's actions column */}
              {actionsColumn && actionsColumn.render && (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  {actionsColumn.render(undefined, record, index) as React.ReactNode}
                </div>
              )}
              
              {/* Fallback to mobileActionItems if actionsColumn is not found */}
              {!actionsColumn && showActions && actionItems && (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Dropdown
                    menu={{
                      items: actionItems(record).map(item => ({
                        key: item.key,
                        label: item.label,
                        icon: item.icon,
                        onClick: item.onClick,
                        danger: item.danger,
                      })),
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentElement || document.body}
                    overlayStyle={{ zIndex: 1050 }}
                  >
                    <Button 
                      type="text" 
                      icon={<MoreOutlined />} 
                      size="small"
                      style={{ minWidth: 32, height: 32 }}
                    />
                  </Dropdown>
                </div>
              )}
            </Space>
          </Card>
        );
      })}
    </Space>
  );
};

/**
 * Responsive data table component
 */
export const ResponsiveDataTable = <T = Record<string, unknown>>({
  dataSource,
  columns,
  loading = false,
  pagination,
  rowKey = 'id' as keyof T,
  size,
  bordered = false,
  maxMobileColumns = 3,
  showMobileActions = false,
  mobileActionItems,
  customCardRenderer,
  ...restProps
}: ResponsiveDataTableProps<T>) => {
  const { isMobile, isTablet, screenWidth } = useResponsive();
  
  // Calculate scrollbar width (difference between innerWidth and clientWidth)
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  
  useEffect(() => {
    const updateScrollbarWidth = () => {
      if (typeof window !== 'undefined') {
        setScrollbarWidth(window.innerWidth - document.documentElement.clientWidth);
      }
    };
    
    updateScrollbarWidth();
    window.addEventListener('resize', updateScrollbarWidth);
    return () => window.removeEventListener('resize', updateScrollbarWidth);
  }, []);

  // Determine table size based on responsiveness
  const actualSize = size || (isMobile ? 'middle' : 'small');

  // Calculate total column width from columns prop
  const calculateTotalColumnWidth = (): number => {
    let total = 0;
    columns.forEach(col => {
      if ('width' in col && col.width) {
        const width = col.width;
        if (typeof width === 'number') {
          total += width;
        } else if (typeof width === 'string' && width.endsWith('px')) {
          total += parseInt(width, 10);
        }
      } else {
        // Default width for columns without specified width (flex columns)
        total += 200;
      }
    });
    return total;
  };

  // Calculate dynamic scroll width for tables
  const calculateScrollWidth = (): number | undefined => {
    if (isTablet) return undefined;
    
    const sidebarWidth = 256;
    const contentPadding = 80;
    const safetyMargin = 20;
    const availableWidth = screenWidth - sidebarWidth - contentPadding - scrollbarWidth - safetyMargin;
    
    const totalColumnWidth = calculateTotalColumnWidth();
    
    if (totalColumnWidth <= availableWidth) {
      return undefined;
    }
    
    return availableWidth;
  };

  // Mobile and Tablet card view
  if (isMobile || isTablet) {
    return (
      <Spin spinning={loading}>
        <MobileCardView
          dataSource={dataSource}
          columns={columns}
          rowKey={rowKey}
          maxColumns={maxMobileColumns}
          showActions={showMobileActions}
          actionItems={mobileActionItems}
          customCardRenderer={customCardRenderer}
        />
        {pagination && typeof pagination !== 'boolean' && (
          <Pagination
            style={{ marginTop: 16, textAlign: 'right' }}
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total || dataSource.length}
            onChange={pagination.onChange}
            onShowSizeChange={pagination.onShowSizeChange}
            simple={isMobile}
            showSizeChanger={pagination.showSizeChanger !== false}
            showQuickJumper={false}
            pageSizeOptions={pagination.pageSizeOptions || ['10', '20', '50', '100']}
          />
        )}
      </Spin>
    );
  }

  const scrollConfig = calculateScrollWidth();

  // Desktop table view
  return (
    <Table<T>
      dataSource={dataSource}
      columns={columns}
      loading={loading}
      pagination={pagination ? {
        ...pagination,
        showSizeChanger: pagination.showSizeChanger !== false && !isMobile,
        showQuickJumper: pagination.showQuickJumper !== false && !isMobile,
        pageSizeOptions: pagination.pageSizeOptions || ['10', '20', '50', '100'],
        simple: isMobile,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      } : false}
      rowKey={rowKey}
      scroll={scrollConfig ? { x: scrollConfig } : undefined}
      size={actualSize}
      bordered={bordered}
      locale={{ emptyText: <Empty description="No Data" /> }}
      {...restProps}
    />
  );
};

export default ResponsiveDataTable;
